import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { ModalTypes } from "@enums/modal-types";
import { resources } from "@constants/hardcoded-strings"
import { POLICY_COMPONENTS_REFERENCE_DATA_TYPES, PolicyComponentsReferenceData } from "@constants/reference-data";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { formatRpServiceConfigurationServiceOfferings } from "@formatters/rp-services/rp-service-configuration-service-offering-formatter";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { getPolicyComponentsReduxRequest } from "@redux-requests/policy-component/get-policy-components";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { CardContainer } from "@containers/common/card-container/card-container"
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body"
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header"
import { useModalContext } from "@context/use-modal-context";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { AddPolicyComponentModal } from "@containers/modals/add-policy-component-modal/add-policy-component-modal";
import { PolicyComponentsTableContainer } from "./policy-components-table-container/policy-components-table-container";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getActiveReferenceData } from "@utils/data-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getPolicyComponentsDataState } from "@redux/selectors/policy-components-selectors";

export const PolicyComponentsContainer = () => {
    const modalContext = useModalContext();
    const [shouldLoadPolicyComponents, setShouldLoadPolicyComponents] = useState(true);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { policyComponents: policyComponentsTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingPolicyComponents,
        policyComponents
    } = useSelector(getPolicyComponentsDataState);
    const referenceData = useReferenceDataSelector<PolicyComponentsReferenceData>(POLICY_COMPONENTS_REFERENCE_DATA_TYPES);

    useEffect(() => {
        if (!referenceData.isLoading.policyComponents && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                POLICY_COMPONENTS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.POLICY_COMPONENTS
            );
        }
    }, [referenceData.isLoading.policyComponents]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.simpleDeployedPolicyComponentConditions &&
            referenceData.axnAcquiredAttributes &&
            referenceData.axnServiceOfferings &&
            referenceData.apAssertions &&
            referenceData.axnAssertions &&
            referenceData.axnAttributeTypeFields &&
            referenceData.axnAttributeTypes &&
            referenceData.axnAvailableAssertionStatuses &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnCompanies,
        referenceData.simpleDeployedPolicyComponentConditions,
        referenceData.axnAcquiredAttributes,
        referenceData.axnServiceOfferings,
        referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAttributeTypeFields,
        referenceData.axnAttributeTypes,
        referenceData.axnAvailableAssertionStatuses,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingPolicyComponents && shouldLoadPolicyComponents) {
            setShouldLoadPolicyComponents(false);
            getPolicyComponentsReduxRequest(dispatch)();
        }
    }, [isReferenceDataPresent, isLoadingPolicyComponents, shouldLoadPolicyComponents]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.policyComponents || isLoadingPolicyComponents;
    }, [referenceData.isLoading.policyComponents, isLoadingPolicyComponents]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            policyComponents &&
            !shouldLoadPolicyComponents &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData
        );
    }, [
        isLoading,
        policyComponents,
        shouldLoadPolicyComponents,
        isReferenceDataPresent,
        shouldLoadReferenceData
    ]);

    const {
        axnCompanies: activeAxnCompanies
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnCompanies } = referenceData;

            return getActiveReferenceData({
                axnCompanies
            });
        }

        return {
            axnCompanies: []
        };
    }, [isReferenceDataPresent, referenceData.axnCompanies]);
    

    const renderAddPolicyComponentModal = useMemo(() => (onModalClose: () => void) => {
        const availableAcquiredAttributeTypeIds = referenceData.axnAcquiredAttributes.map(attr => attr.attributeTypeID);
        const axnAcquiredAttributeTypeFields = referenceData.axnAttributeTypeFields.filter(atf => availableAcquiredAttributeTypeIds.includes(atf.attributeTypeID));

        const serviceOfferings = formatRpServiceConfigurationServiceOfferings(
            [],
            referenceData.axnServiceOfferings,
            referenceData.apAssertions,
            referenceData.axnAssertions,
            [],
            []
        ).map(so => ({
            ...so,
            addedToService: true,
            status: EntityStatusTypes.ACTIVE
        }));

        const assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion> = [];
        serviceOfferings.forEach(so => {
            assertions.push(...so.assertions);
        });

        return (
            <AddPolicyComponentModal
                axnCompanies={activeAxnCompanies}
                allPolicyComponents={policyComponents}
                policyComponents={policyComponents}
                policyComponentConditions={referenceData.simpleDeployedPolicyComponentConditions}
                assertions={assertions}
                availableAssertions={assertions}
                acquiredAttributes={referenceData.axnAcquiredAttributes}
                serviceOfferings={serviceOfferings}
                allAxnAttributeTypes={referenceData.axnAttributeTypes}
                axnAttributeTypes={referenceData.axnAttributeTypes}
                allAxnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                axnAcquiredAttributeTypeFields={axnAcquiredAttributeTypeFields}
                axnAvailableAssertionStatuses={referenceData.axnAvailableAssertionStatuses}
                updateCommonReferenceData={false}
                onModalClose={onModalClose}
                setPolicyComponent={() => { }}
            />
        );
    }, [
        policyComponents,
        activeAxnCompanies,
        referenceData.simpleDeployedPolicyComponentConditions,
        referenceData.axnAttributeTypeFields,
        referenceData.axnServiceOfferings,
        referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAcquiredAttributes,
        referenceData.axnAvailableAssertionStatuses
    ]);

    const headerButton = useMemo(() => {
        if (isLoaded && session.userPermissions.canCreatePolicyComponents) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddPolicyComponent,
                    renderFunction: renderAddPolicyComponentModal
                });
            };
            return (
                <ButtonWithIcon
                    id={"add-policy-component-button"}
                    title={resources.pages.policyComponents.headerButton}
                    text={resources.pages.policyComponents.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        }
        return null;
    }, [
        isLoading,
        isLoaded,
        session,
        renderAddPolicyComponentModal
    ]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.policyComponents.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"policy-component-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <PolicyComponentsTableContainer
                        policyComponents={policyComponents}
                        companies={referenceData.axnCompanies}
                        tableState={policyComponentsTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
}